import { apiGet } from './client';
import type { OperationResult, WorkflowCatalogAgent, WorkflowCatalogContext } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Workflow diagram — graph payload for one [CodeFirstWorkflow]
// ─────────────────────────────────────────────────────────────────────────────
//
// Pairs with backend WorkflowDiagramService. Backend resolves the workflow
// through Option C (per-merchant override → global override → code-first
// default) and reflects on each executor's [WorkflowExecutorDoc] +
// [SendsMessage(typeof(T))] to produce nodes + edges with rich metadata.
//
// Frontend uses this payload to drive the React Flow diagram on
// WorkflowDetailPage. Live-highlight wiring overlays the static graph
// with useWorkflowProgress events when a run is in flight.
// ─────────────────────────────────────────────────────────────────────────────

export type WorkflowDiagramSource =
  | 'merchant_override'
  | 'global_override'
  | 'code_first'
  | 'unknown';

export interface WorkflowDiagramNode {
  /** Stable id matching WorkflowNode.id — used by useWorkflowProgress to highlight live. */
  id: string;
  classId: string;
  isStart: boolean;
  isAgentNode: boolean;
  agentRefId: string | null;
  /** The agent's descriptive title and model, when known (backend WorkflowCatalogManager). */
  agentName?: string | null;
  agentModel?: string | null;
  /** Display title from [WorkflowExecutorDoc.Title]; falls back to id when absent. */
  title: string;
  /** One-sentence description from [WorkflowExecutorDoc.Description]. */
  description: string | null;
  /** Icon hint from [WorkflowExecutorDoc.Icon] (Lucide / Ant Design name). */
  icon: string | null;
  /** Category tag — drives color coding ("Input"|"Agent"|"Persistence"|"Notify"|...). */
  category: string | null;
  /** The rules the step enforces, one short sentence each. */
  checks?: string[];
  /** The agent an agent step runs — name, model, tools and what it loads; null for a code step. */
  agent?: WorkflowCatalogAgent | null;
}

export interface WorkflowDiagramEdge {
  source: string;
  target: string;
  /** Type name of the typed message the source emits, derived from [SendsMessage(typeof(T))]. */
  messageType: string | null;
  /** "EDGE" | "CHAIN" | "FANOUT" — from WorkflowNode.Type; "CONDITIONAL" for a conditional edge. */
  edgeKind: string;
  /** What travels along the arrow, in plain words (backend WorkflowDiagramService.LabelFor). */
  label?: string | null;
}

export interface WorkflowDiagram {
  name: string;
  title: string;
  source: WorkflowDiagramSource;
  nodes: WorkflowDiagramNode[];
  edges: WorkflowDiagramEdge[];
  /** What starts a run, in plain words; null when the workflow does not say. */
  startsWhen?: string | null;
  /** What the start step is handed. */
  startReceives?: string | null;
  /** What a finished run leaves behind. */
  results?: string[];
  /** The contexts handed between steps — drawn beside them. */
  contexts?: WorkflowCatalogContext[];
}

/**
 * Fetch the diagram for the named workflow (resolved per current merchant).
 * Returns null on 404 (no row + no code-first default registered).
 */
export async function getWorkflowDiagram(name: string): Promise<WorkflowDiagram | null> {
  try {
    const wrapped = await apiGet<OperationResult<WorkflowDiagram>>(
      `/api/AIProcessLibrary/Diagram?name=${encodeURIComponent(name)}`,
    );
    if (!wrapped.isSuccess || !wrapped.data) return null;
    return wrapped.data;
  } catch {
    return null;
  }
}
